import { useEffect, useState } from "react";
import { api, fmtDate, fmtMoney } from "@/lib/api";
import { Plus, X } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";

export default function Invoices() {
  const [tab, setTab] = useState("client");
  const [clientInvoices, setClientInvoices] = useState([]);
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [irnView, setIrnView] = useState(null); // { invoice_id, irn, ack_no, qr_payload, qr_png_data_url }

  const load = async () => {
    const [ci, pi, cs, cl, co] = await Promise.all([
      api.get("/invoices/client"), api.get("/invoices/contractor"),
      api.get("/cases"), api.get("/clients"), api.get("/contractors")
    ]);
    setClientInvoices(ci.data); setContractorInvoices(pi.data);
    setCases(cs.data); setClients(cl.data); setContractors(co.data);
  };
  useEffect(() => { load(); }, []);

  const items = tab === "client" ? clientInvoices : contractorInvoices;

  const markPaid = async (id) => {
    await api.post(`/invoices/client/${id}/mark-paid`);
    load();
  };

  const downloadPdf = async (id, num) => {
    const { data } = await api.get(`/invoices/client/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `invoice_${(num || id).replace(/\//g, "-")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const generateIrn = async (id) => {
    try {
      const { data } = await api.post(`/finance/invoices/${id}/generate-irn`);
      setIrnView(data);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to issue IRN");
    }
  };

  const emailInvoice = async (id, invNum) => {
    const def = prompt(`Send invoice ${invNum} to which email address?\n(Press Enter to use the client's saved email.)`, "");
    if (def === null) return; // cancelled
    try {
      const { data } = await api.post(`/invoices/client/${id}/email`, def ? { to_email: def } : {});
      alert(`Sent to ${data.to} · Resend id ${data.resend_id || "—"}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to email invoice");
    }
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Billing</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Invoices</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            const path = tab === "client" ? "/export/invoices.csv" : "/export/payables.csv";
            const { data } = await api.get(path, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a"); a.href = url; a.download = tab + ".csv";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} className="span-btn span-btn-ghost">Export CSV</button>
          <button data-testid="new-invoice-btn" onClick={() => setOpen(true)} className="span-btn">
            <Plus size={14} /> New {tab} invoice
          </button>
        </div>
      </div>

      <div className="border-b border-[var(--span-border)] flex gap-6">
        {["client", "contractor"].map(t => (
          <button key={t} data-testid={`invoice-tab-${t}`} onClick={() => setTab(t)}
            className={`py-3 text-[12px] uppercase font-medium tracking-[0.16em] border-b-2 ${tab === t ? "border-black" : "border-transparent text-[var(--span-text-2)]"}`}>
            {t === "client" ? `client (${clientInvoices.length})` : `supplier (${contractorInvoices.length})`}
          </button>
        ))}
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead>
            <tr>
              {tab === "client" ? <th>Invoice #</th> : <th>Supplier</th>}
              <th>Case</th>
              <th>Currency</th>
              <th className="text-right">Amount</th>
              {tab === "client" && <th className="text-right">Total (incl. GST)</th>}
              <th>Status</th>
              <th>Created</th>
              {tab === "client" && <th></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[12px] text-[var(--span-text-2)]">No invoices.</td></tr>}
            {items.map(i => (
              <tr key={i.invoice_id}>
                <td className="font-mono">{tab === "client" ? i.invoice_number : i.contractor_name}</td>
                <td className="font-mono text-[11px]">{i.case_id?.slice(0, 12)}…</td>
                <td className="font-mono">{i.currency}</td>
                <td className="text-right tnum font-mono">{fmtMoney(i.amount, i.currency)}</td>
                {tab === "client" && <td className="text-right tnum font-mono font-semibold">{fmtMoney(i.total, i.currency)}</td>}
                <td><span className={`badge ${i.status === "paid" ? "badge-success" : i.status === "hold" ? "badge-warn" : "badge-info"}`}>{i.status}</span></td>
                <td className="text-[var(--span-text-2)]">{fmtDate(i.created_at)}</td>
                {tab === "client" && (
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => downloadPdf(i.invoice_id, i.invoice_number)} data-testid={`pdf-${i.invoice_id}`} className="span-btn span-btn-ghost h-7 text-[11px]">PDF</button>
                      <button onClick={() => emailInvoice(i.invoice_id, i.invoice_number)} data-testid={`email-${i.invoice_id}`} className="span-btn span-btn-ghost h-7 text-[11px]" title={i.last_emailed_at ? `Last emailed ${i.last_emailed_at?.slice(0,10)} to ${i.last_emailed_to}` : "Email this invoice to the client"}>
                        {i.last_emailed_at ? "Email ✓" : "Email"}
                      </button>
                      {i.status !== "paid" && (
                        <button onClick={() => markPaid(i.invoice_id)} data-testid={`mark-paid-${i.invoice_id}`} className="span-btn span-btn-ghost h-7 text-[11px]">Mark paid</button>
                      )}
                      {i.currency === "INR" && (
                        i.irn ? (
                          <button onClick={() => setIrnView({ invoice_id: i.invoice_id, irn: i.irn, ack_no: i.irn_ack_no, qr_payload: i.irn_qr_payload, qr_png_data_url: i.irn_qr_png_base64 ? `data:image/png;base64,${i.irn_qr_png_base64}` : null })} data-testid={`irn-view-${i.invoice_id}`} className="span-btn span-btn-ghost h-7 text-[11px]">IRN ✓</button>
                        ) : (
                          <button onClick={() => generateIrn(i.invoice_id)} data-testid={`irn-issue-${i.invoice_id}`} className="span-btn span-btn-ghost h-7 text-[11px]">Issue IRN</button>
                        )
                      )}
                      {!i.deleted_at && (
                        <DeleteWorkflowButton
                          entityType="client_invoice"
                          entityId={i.invoice_id}
                          entityLabel={i.invoice_number}
                          onRequested={load}
                        />
                      )}
                      {i.deleted_at && <span className="badge badge-danger text-[9px]">deleted</span>}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <NewInvoice tab={tab} cases={cases} clients={clients} contractors={contractors} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {irnView && <IrnModal data={irnView} onClose={() => setIrnView(null)} />}
    </div>
  );
}

function IrnModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="irn-modal">
      <div className="card-flat w-full max-w-[520px]">
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">GST e-Invoice</div>
            <div className="font-display font-bold text-[17px]">IRN issued · {data.invoice_id?.slice(0, 12)}…</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="label-eyebrow mb-1">IRN (64 hex)</div>
            <div className="font-mono text-[10px] break-all border border-[var(--span-border)] p-2 bg-[var(--span-bg-2)]" data-testid="irn-value">{data.irn}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1">Ack No</div>
              <div className="font-mono text-[11px]">{data.ack_no}</div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Ack Date</div>
              <div className="font-mono text-[11px]">{(data.ack_date || "").slice(0, 19)}</div>
            </div>
          </div>
          {data.qr_png_data_url && (
            <div>
              <div className="label-eyebrow mb-1">Signed QR (NIC spec)</div>
              <img src={data.qr_png_data_url} alt="GST e-Invoice QR" className="w-[200px] h-[200px] border border-[var(--span-border)]" data-testid="irn-qr-img" />
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-1">QR payload</div>
            <div className="font-mono text-[10px] break-all border border-[var(--span-border)] p-2 bg-[var(--span-bg-2)]">{data.qr_payload}</div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[var(--span-border)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="span-btn">Done</button>
        </div>
      </div>
    </div>
  );
}

function NewInvoice({ tab, cases, clients, contractors, onClose, onCreated }) {
  const [form, setForm] = useState({
    case_id: cases[0]?.case_id || "",
    client_id: clients[0]?.client_id || "",
    contractor_id: contractors[0]?.contractor_id || "",
    amount: 1000, reimbursement_amount: 0, currency: "INR", gst_pct: 18, po_number: "", description: ""
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const path = tab === "client" ? "/invoices/client" : "/invoices/contractor";
      const body = tab === "client"
        ? { case_id: form.case_id, client_id: form.client_id, amount: Number(form.amount), reimbursement_amount: Number(form.reimbursement_amount), currency: form.currency, gst_pct: Number(form.gst_pct), po_number: form.po_number, description: form.description }
        : { case_id: form.case_id, contractor_id: form.contractor_id, amount: Number(form.amount), currency: form.currency, description: form.description };
      await api.post(path, body);
      onCreated();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="card-flat w-full max-w-[520px]">
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">New {tab} invoice</div>
            <div className="font-display font-bold text-[17px]">Create</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Case</label>
            <select required value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })} className="span-input">
              {cases.map(c => <option key={c.case_id} value={c.case_id}>{c.case_code} — {c.title.slice(0, 40)}</option>)}
            </select>
          </div>
          {tab === "client" ? (
            <div>
              <label className="label-eyebrow block mb-1.5">Client</label>
              <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="span-input">
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="label-eyebrow block mb-1.5">Supplier</label>
              <select required value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} className="span-input">
                {contractors.map(c => <option key={c.contractor_id} value={c.contractor_id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Services</label>
              <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="span-input font-mono" data-testid="invoice-services-amount" />
            </div>
            {tab === "client" && (
              <div>
                <label className="label-eyebrow block mb-1.5">Reimbursements</label>
                <input type="number" value={form.reimbursement_amount} onChange={(e) => setForm({ ...form, reimbursement_amount: e.target.value })} className="span-input font-mono" data-testid="invoice-reimbursement-amount" />
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-1.5">Currency</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="span-input">
                {["INR", "USD", "SGD", "EUR", "BDT", "EGP", "CHF", "AED", "GBP"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {tab === "client" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">GST %</label>
                <input type="number" step="0.1" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} className="span-input font-mono" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">PO #</label>
                <input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="span-input font-mono" />
              </div>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1.5">Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="span-input" />
          </div>
          {err && <div className="text-[12px] text-[var(--span-danger)]">{String(err)}</div>}
        </div>
        <div className="px-5 py-4 border-t border-[var(--span-border)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button disabled={busy} className="span-btn" data-testid="submit-invoice">{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}
